<script setup>
/**
 * CompetitionQuestionsPrompt: the first time a member opens a competition
 * that has questions waiting for them, one dialog asks them all.
 *
 * What is waiting comes from the API (GET competitionQuestions/{type}/{id}),
 * which decides who is asked (they play in it, the question applies, they
 * have not answered). Answers go back with PUT; once answered the API stops
 * returning the question. Replaces the Squash Levels-only prompt, which is now
 * the first question in the API's registry.
 *
 * Closing the dialog without saving (the close control or Escape) is a NO to
 * every question on it (owner decision, 21 Sep 2026): the Squash Levels
 * account default is ON, and an unanswered close must not leave a member
 * opted in by a default. Escape closes like the close control, for keyboard
 * users (tester finding, 25 Sep 2026); a click outside still does nothing,
 * so a stray click cannot answer for them.
 *
 * Drawn by CompetitionQuestionsDialog, the shell Turn on uses too (owner,
 * 3 Nov 2026): with ONE question waiting, its heading in the header, its logo
 * big and centred, and the button follows the switch ("Don't share" / "Share
 * my results"). With several, "Before you start" and "Save".
 *
 * Mount on a competition page with its type and id. Client only: nothing is
 * fetched or armed during a server render.
 */
import {onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {endpoints} from '~/network/endpoints.js';
import {useHttpRequest} from '~/composables/useHttpRequest';
import {useAuthService} from '~/composables/useAuthService';
import {
  competitionAnswers,
  competitionAnswersOnClose,
  competitionQuestionsAsked as asked,
  competitionQuestionsKey,
  pendingQuestions,
} from '~/composables/useCompetitionQuestions';
import CompetitionQuestionsDialog from '~/components/competitions/CompetitionQuestionsDialog.vue';
import {competitionQuestionsOpen} from '~/composables/useInterruptions';

const props = defineProps({
  competitionType: {type: String, default: null},
  competitionId: {type: String, default: null},
});

const {user, updateLocalUser} = useAuthService();
const {httpRequest} = useHttpRequest();

const open = ref(false);
// Shared with PromptIntroDock, which waits while this is on screen: two
// interruptions at once (the question and the news nudge) read as a pile-up.
watch(open, (v) => { competitionQuestionsOpen.value = v; });
onBeforeUnmount(() => { competitionQuestionsOpen.value = false; });
const saving = ref(false);
const error = ref(null);
const title = ref('');
const questions = ref([]);
const ticked = ref({});

let checking = false;
const check = async () => {
  if (import.meta.server || checking || open.value) return;
  if (!user.value?.id || !props.competitionType || !props.competitionId) return;
  const key = competitionQuestionsKey(user.value.id, props.competitionType, props.competitionId);
  if (asked.has(key)) return;
  checking = true;
  try {
    const res = await httpRequest(endpoints.competitionQuestions.pending(props.competitionType, props.competitionId));
    const list = pendingQuestions(res);
    if (!list.length) return; // nothing now; they may join later, so do not remember it
    asked.add(key);
    questions.value = list;
    title.value = res.title || 'Before you start';
    ticked.value = {};
    open.value = true;
  } catch (e) {
    // Nothing to ask is the safe failure: the page asks again on the next visit.
  } finally {
    checking = false;
  }
};
onMounted(check);
watch(() => [user.value?.id, props.competitionType, props.competitionId], check);

const save = async (answers) => {
  saving.value = true;
  error.value = null;
  try {
    await httpRequest(endpoints.competitionQuestions.answer(props.competitionType, props.competitionId), {joining: false, answers});
    // An account-wide answer (Squash Levels) shows under Your profile at once.
    await updateLocalUser();
    open.value = false;
  } catch (e) {
    error.value = e?.global?.[0] || "Couldn't save that. Please try again.";
  } finally {
    saving.value = false;
  }
};

const onDialogModel = (value) => {
  if (value || saving.value) return;
  save(competitionAnswersOnClose(questions.value));
};
</script>

<template>
  <competition-questions-dialog :model-value="open" @update:model-value="onDialogModel" v-model:ticked="ticked"
                                :questions="questions" :title="title" :saving="saving" :error="error"
                                @save="save(competitionAnswers(questions, ticked))"/>
</template>
